import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Package, Thermometer, Clock, MapPin, ChevronRight, MessageCircle, Phone } from "lucide-react";
import { PRODUCTS } from "@/data";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCTS.find(p => p.id === slug) || null;
  const relatedProducts = product
    ? PRODUCTS.filter(p => p.category === product.category && p.id !== slug).slice(0, 4)
    : [];

  if (!product) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center" data-testid="product-not-found">
          <h2 className="text-2xl font-bold text-gray-400 mb-4" style={{ fontFamily: 'Montserrat' }}>Product Not Found</h2>
          <Link to="/products">
            <Button className="rounded-full bg-[#2B7A1E] hover:bg-[#236618] text-white">Back to Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  const whatsappMsg = encodeURIComponent(`Hi, I'm interested in ${product.name}. Please share more details including pricing and availability.`);

  return (
    <div className="pt-20" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <section className="py-4 bg-[#f8f8f8] border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <nav className="flex items-center gap-2 text-sm text-gray-500" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#2B7A1E]">Home</Link>
            <ChevronRight size={14} />
            <Link to="/products" className="hover:text-[#2B7A1E]">Products</Link>
            <ChevronRight size={14} />
            <Link to={`/products?category=${product.category}`} className="hover:text-[#2B7A1E]">{product.categoryName}</Link>
            <ChevronRight size={14} />
            <span className="text-[#333] font-medium truncate">{product.name}</span>
          </nav>
        </div>
      </section>

      {/* Product Detail */}
      <section className="py-10 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="rounded-3xl overflow-hidden shadow-lg sticky top-24">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full aspect-square object-cover"
                  data-testid="product-image"
                />
              </div>
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Badge className="bg-green-100 text-[#2B7A1E] hover:bg-green-100 mb-3 px-3 py-1 text-xs font-semibold rounded-full border-0">
                {product.categoryName}
              </Badge>
              {product.featured && (
                <Badge className="bg-[#D94F04] text-white border-0 mb-3 ml-2 px-3 py-1 text-xs">Featured Product</Badge>
              )}

              <h1 className="text-3xl md:text-4xl font-bold text-[#333] tracking-tight mb-4" style={{ fontFamily: 'Montserrat' }} data-testid="product-name">
                {product.name}
              </h1>

              <p className="text-gray-600 leading-relaxed mb-6" data-testid="product-description">
                {product.description}
              </p>

              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { icon: <Package size={16} />, label: "Form", value: product.form },
                  { icon: <Thermometer size={16} />, label: "Storage", value: product.storageTemp },
                  { icon: <Clock size={16} />, label: "Shelf Life", value: product.shelfLife },
                  { icon: <MapPin size={16} />, label: "Origin", value: product.origin },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-2.5 p-3 bg-[#f8f8f8] rounded-xl">
                    <span className="text-[#2B7A1E] mt-0.5">{item.icon}</span>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">{item.label}</p>
                      <p className="text-sm font-medium text-[#333]">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {product.brixLevel && (
                <div className="mb-6 p-3 bg-yellow-50 rounded-xl border border-yellow-100">
                  <p className="text-sm"><span className="font-semibold text-[#333]">Brix Level:</span> <span className="text-gray-600">{product.brixLevel}&deg;</span></p>
                </div>
              )}

              {/* Packaging */}
              <div className="mb-6">
                <h3 className="text-sm font-bold text-[#333] mb-2 uppercase tracking-wide" style={{ fontFamily: 'Montserrat' }}>Available Packaging</h3>
                <div className="flex flex-wrap gap-2">
                  {product.packaging?.map((pkg) => (
                    <Badge key={pkg} variant="outline" className="rounded-full px-3 py-1.5 text-sm border-gray-200 text-gray-600">
                      {pkg}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-3 mb-8">
                <a href={`https://wa.me/918308203097?text=${whatsappMsg}`} target="_blank" rel="noopener noreferrer">
                  <Button className="rounded-full bg-[#2B7A1E] hover:bg-[#236618] text-white font-semibold px-8 h-12 shadow-lg shadow-green-900/15" data-testid="product-enquire-btn">
                    <MessageCircle size={18} className="mr-2" /> Enquire Now
                  </Button>
                </a>
                <a href="tel:+918308203097">
                  <Button variant="outline" className="rounded-full border-2 border-[#2B7A1E] text-[#2B7A1E] font-semibold px-6 h-12 hover:bg-green-50" data-testid="product-call-btn">
                    <Phone size={18} className="mr-2" /> Call Us
                  </Button>
                </a>
              </div>

              <Separator className="my-6" />

              {/* Tabs */}
              <Tabs defaultValue="applications" data-testid="product-tabs">
                <TabsList className="bg-[#f8f8f8] rounded-full p-1 w-full flex">
                  <TabsTrigger value="applications" className="rounded-full flex-1 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm">Applications</TabsTrigger>
                  <TabsTrigger value="nutrition" className="rounded-full flex-1 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm">Nutrition</TabsTrigger>
                  <TabsTrigger value="features" className="rounded-full flex-1 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm">Features</TabsTrigger>
                </TabsList>

                <TabsContent value="applications" className="mt-6">
                  <div className="grid grid-cols-2 gap-3">
                    {product.applications?.map((app) => (
                      <div key={app} className="flex items-center gap-2 p-3 bg-[#f8f8f8] rounded-xl">
                        <div className="w-2 h-2 rounded-full bg-[#2B7A1E]" />
                        <span className="text-sm text-gray-700">{app}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="nutrition" className="mt-6">
                  {product.nutritionalInfo && (
                    <div className="bg-[#f8f8f8] rounded-2xl p-6">
                      <h4 className="text-sm font-bold text-[#333] mb-4" style={{ fontFamily: 'Montserrat' }}>Nutritional Information (per 100g)</h4>
                      <div className="space-y-2.5">
                        {Object.entries(product.nutritionalInfo).map(([key, val]) => (
                          <div key={key} className="flex justify-between items-center py-1.5 border-b border-gray-200 last:border-0">
                            <span className="text-sm text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                            <span className="text-sm font-semibold text-[#333]">{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="features" className="mt-6">
                  <div className="grid grid-cols-2 gap-3">
                    {product.features?.map((feat) => (
                      <div key={feat} className="flex items-center gap-2 p-3 bg-green-50 rounded-xl">
                        <div className="w-5 h-5 rounded-full bg-[#2B7A1E] flex items-center justify-center">
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </div>
                        <span className="text-sm text-gray-700">{feat}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-14 bg-[#f8f8f8]" data-testid="related-products">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <h2 className="text-2xl font-bold text-[#333] mb-8" style={{ fontFamily: 'Montserrat' }}>Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((rp) => (
                <Link key={rp.id} to={`/products/${rp.id}`} data-testid={`related-${rp.id}`}>
                  <div className="group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    <div className="aspect-square product-img-zoom">
                      <img src={rp.image} alt={rp.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-bold text-[#333] group-hover:text-[#2B7A1E] transition-colors" style={{ fontFamily: 'Montserrat' }}>{rp.name}</h3>
                      <span className="text-xs text-gray-400">{rp.form}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-12 bg-[#2B7A1E]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat' }}>
            Interested in {product.name}?
          </h2>
          <p className="text-white/80 mb-6">Get pricing, samples, and custom packaging options for your business.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contact">
              <Button className="rounded-full bg-white text-[#2B7A1E] font-bold px-8 h-11 hover:bg-gray-100" data-testid="product-bottom-contact">
                Request a Quote
              </Button>
            </Link>
            <a href={`https://wa.me/918308203097?text=${whatsappMsg}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full border-2 border-white text-white font-semibold px-8 h-11 hover:bg-white/10" data-testid="product-bottom-whatsapp">
                WhatsApp Inquiry
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
